import cs from 'classnames';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RiCloseFill } from 'react-icons/ri';

import { useFetchAutocompleteTagMutation, useFetchSimilarTagsMutation } from '@/api/hooks/useCategory';
import { TagResponse } from '@/api/lib/category';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';

import InputGroup from '../../core/InputGroup';
import InputGroupHorizontal from '../../core/InputGroupHorizontal';
import { Spinner } from '@/components/atoms/Spinner';
import { Typography } from '@/components/atoms/Typography';
import { FontWeight } from '@/components/atoms/Typography/interface';
import { AutoCompleteList } from '@/components/molecules/AutoCompleteList';
import { FlattenedSuggestion } from '@/components/molecules/AutoCompleteList/utils';

import { SelectionTag } from './SelectionTag';
import { Tags } from './Tags';
import { createSimilarTagSuggestions, createTagSuggestions } from './utils';

type Props = {
  horizontal?: boolean;
  label?: string;
  searchTagInputId: string;
  searchTagInputIds: string;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  type?: 'text';
  readOnly?: boolean;
  inputOptions?: RegisterOptions;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  defaultValue?: string;
  tooltipMessage?: string;
  weight?: FontWeight;
};

export type Tag = { text: string; id: string };

export type AutoCompleteTagHandle = {
  setSuggestions: (tags: Tag[]) => void;
  addNewSuggestion: (tag: Tag) => void;
  resetSuggestions: () => void;
};

export const AutoCompleteTagInput = forwardRef<AutoCompleteTagHandle, Props>(
  (
    {
      horizontal,
      label,
      placeholder = '',
      helperText,
      defaultValue,
      searchTagInputId,
      searchTagInputIds,
      type = 'text',
      readOnly = false,
      inputOptions,
      leftIcon,
      required = false,
      rightIcon,
      tooltipMessage,
      weight
    },
    ref
  ) => {
    const { t } = useTranslation();

    const {
      register,
      formState: { errors },
      watch,
      setValue,
      setError,
      clearErrors
    } = useFormContext();
    const { onChange: registerOnChange, ...registerProps } = register(searchTagInputId, inputOptions);
    const formError = errors[searchTagInputIds];
    const Group = horizontal ? InputGroupHorizontal : InputGroup;
    const specialtiesInput = watch(searchTagInputId);

    const [multipleValues, setMultipleValues] = useState<Tag[]>([]);
    const [tagData, setTagData] = useState<TagResponse[]>([]);
    const [similarTagsData, setSimilarTagsData] = useState<TagResponse[]>([]);

    const addMultipleValues = (tag: Tag) => {
      if (multipleValues.length === 10) {
        setError(searchTagInputId, {
          message: t('form.maximumSelectionSpecialties')
        });
        return;
      }
      if (formError?.message) {
        clearErrors([searchTagInputId, searchTagInputIds]);
      }
      if (multipleValues.find((value) => value.id === tag.id)) {
        return;
      }
      const newMultipleValues = [...multipleValues, tag];
      const tagIds = newMultipleValues.map((value) => value.id);

      setMultipleValues(newMultipleValues);
      setValue(searchTagInputIds, tagIds, { shouldTouch: true, shouldDirty: true });
    };

    const handleRemoveTag = (id: string) => {
      const newMultipleValues = multipleValues.filter((value) => value.id !== id);
      const newTagIds = newMultipleValues.map((value) => value.id);
      setMultipleValues(newMultipleValues);
      setValue(searchTagInputIds, newTagIds, { shouldTouch: true, shouldDirty: true });
      if (formError?.message) {
        clearErrors(id);
      }
    };

    const { isLoading, mutate: fetchAutocompleteTagMutation } = useFetchAutocompleteTagMutation({
      onSuccess: (data) => {
        setTagData(data);
      }
    });

    const { mutate: fetchSimilarTagsMutation } = useFetchSimilarTagsMutation({
      onSuccess: (data) => {
        setSimilarTagsData(data);
      }
    });

    const handleFetchSimilarTags = (id: string) => {
      fetchSimilarTagsMutation({ id });
    };

    const handleClearInputSpecialties = () => {
      setValue(searchTagInputId, '');
      setTagData([]);
    };

    const handleSelectTag = (suggestion: FlattenedSuggestion) => {
      handleFetchSimilarTags(suggestion.id);
      addMultipleValues(suggestion);
      handleClearInputSpecialties();
    };

    const debounceAutoCompleteSpecialtiesChange = useDebouncedCallback((specialties: string) => {
      if (specialties && specialties.length >= 2) {
        fetchAutocompleteTagMutation({
          search: specialties
        });
      } else {
        setTagData([]);
      }
    }, 200);

    useImperativeHandle(ref, () => ({
      setSuggestions(tags: Tag[]) {
        const tagIds = tags.map((value) => value.id);
        setMultipleValues(tags);
        setValue(searchTagInputIds, tagIds);
      },
      addNewSuggestion(tag: Tag) {
        addMultipleValues(tag);
      },
      resetSuggestions() {
        setMultipleValues([]);
        setValue(searchTagInputId, null);
        setValue(searchTagInputIds, null);
      }
    }));

    return (
      <Group
        id={searchTagInputId}
        required={required}
        label={label}
        helperText={helperText}
        error={formError?.message as unknown as string | undefined}
        center
        tooltipMessage={tooltipMessage}
        weight={weight}
      >
        {isLoading && (
          <div className="absolute top-1 right-2">
            <Spinner reverse={true} />
          </div>
        )}
        {specialtiesInput && !isLoading && (
          <RiCloseFill size={20} color="black" className="absolute top-2 right-2" onClick={() => handleClearInputSpecialties()} />
        )}
        <div className="flex flex-row w-80 md:w-full md:max-w-6xl overflow-auto">
          {multipleValues.length > 0 && (
            <div className="flex flex-row justify-start items-center w-auto border border-gray-200 rounded-md border-r-0 rounded-r-none ">
              {multipleValues.map((value) => (
                <SelectionTag key={value.id} id={value.id} onClick={handleRemoveTag} tag={value.text} />
              ))}
            </div>
          )}
          <input
            data-testid={`input-${searchTagInputId}`}
            type={type}
            id={searchTagInputId}
            readOnly={readOnly}
            className={cs('error', [leftIcon && 'pl-10', rightIcon && 'pr-10', multipleValues.length > 0 && 'rounded-l-none border-l-0'], 'p-2 rounded-md')}
            placeholder={placeholder}
            autoComplete="one-time-code"
            aria-describedby={searchTagInputId}
            defaultValue={defaultValue}
            onChange={(event) => {
              debounceAutoCompleteSpecialtiesChange(event.target.value);
              registerOnChange(event);
            }}
            {...registerProps}
          />
        </div>
        {tagData && tagData.length > 0 && (
          <AutoCompleteList suggestions={createTagSuggestions(tagData) ?? []} match={specialtiesInput} onSelectSuggestion={handleSelectTag} />
        )}
        {similarTagsData && similarTagsData.length > 0 && (
          <div className="mt-1">
            <Typography underline align="left" variant="paragraph" color="gray">
              {t('form.restaurantHasSpecialties')}
            </Typography>
            <Tags tags={createSimilarTagSuggestions(similarTagsData) ?? []} onClick={addMultipleValues} />
          </div>
        )}
      </Group>
    );
  }
);
